import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { CardHeader, Stack, Tooltip } from "@mui/material";
import { GridActionsCellItem } from '@mui/x-data-grid-pro';
import DeleteIcon from '@mui/icons-material/Delete';

import {
    DataGrid,
    CommonModal,
    Select,
    PrimaryButton,
    PageTitleWithToolTip,
} from '../Common';
import { text_commonCellProps } from '../Common/DataGrid/utils';

import Routes from '../../routes';
import UserInfo from '../../UserInfo';
import useViewport from '../../hooks/useViewport';
import { Grey2 } from '../../color_utils';

const Providers = ({
    id,
}) => {
    const [dataRefresh, setDataRefresh] = useState(crypto.randomUUID());
    const [isVisible, setVisible] = useState(false);
    // US 16739: reactive so the mobile exclusions update on resize/orientation.
    const { isMobile } = useViewport();

    const [locationOptions, setLocationOptions] = useState([]);
    const [location, setLocation] = useState(null);
    const [locationHasError, setLocationHasError] = useState(false);

    const onVisibleChangeState = () => setVisible(prev => !prev);

    const onRevokeLocation = async (row) => {
        const response = await fetch(Routes.Location.RevokeServiceProvider(), {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                servicerInstanceId: row.id,
                locationId: id,
            })
        });

        if (response.status === 200) {
            setDataRefresh(crypto.randomUUID());
            toast.success('The service provider has been successfully revoked from this location.', { duration: 6000 });
        }
    }

    const onSubmit = async () => {

        if (!location) {
            setLocationHasError(true);
            return;
        }

        const response = await fetch(Routes.Location.AssignServiceProvicer(), {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                servicerInstanceId: location,
                locationId: id,
            })
        });

        if (response.status === 200) {
            setLocation(null);
            setLocationHasError(false);
            setDataRefresh(crypto.randomUUID());
            onVisibleChangeState();
            toast.success('The service provider has been successfully assigned to this location.', { duration: 6000 });
        }
    }

    const fetchAvailableProviders = useCallback(async () => {
        const response = await fetch(Routes.Location.GetAvailableServiceProvider(id));
        const data = await response.json();

        const filteredData = data.map(x => x.servicerInstanceNavigation);

        if (filteredData) {
            setLocationOptions(
                filteredData.map(x => ({
                    id: x.id,
                    name: x.name
                }))
            );
        }
    }, [id, dataRefresh])

    useEffect(() => {
        fetchAvailableProviders();
    }, [fetchAvailableProviders])

    const columns = [
        { field: 'id', headerName: 'ID', ...text_commonCellProps },
        { field: 'name', headerName: 'Name', ...text_commonCellProps },
        { field: 'grant', headerName: 'Grant Type', ...text_commonCellProps },
    ];

    // Revoke lives in a "Quick Actions" column, shown per-row only where allowed.
    const canRevoke = UserInfo.isSystemAdministrator() || UserInfo.isNativeUser();
    const actionRow = {
        type: 'actions',
        field: 'Quick Actions',
        width: 100,
        getActions: ({ row }) => (
            (canRevoke && row.grant === 'Specific Location')
                ? [
                    <GridActionsCellItem
                        key="revoke"
                        label="Revoke"
                        icon={
                            <Tooltip title="Revoke">
                                <DeleteIcon sx={{ color: Grey2 }} />
                            </Tooltip>
                        }
                        onClick={() => onRevokeLocation(row)}
                    />,
                ]
                : []
        ),
    };

    // US 16739: "Assign Service Provider" CTA + the revoke Quick-Actions column are
    // excluded on the mobile breakpoint (view-only table there).
    const canAssign = UserInfo.isNativeSystemOwner()
        && (UserInfo.isSystemAdministrator() || UserInfo.isSuperTenantAdmin() || UserInfo.isTenantAdministrator());
    const showAssign = !isMobile && canAssign;

    return (
        <>
            <DataGrid
                hideExportButton
                url={Routes.Location.ServiceProviderTable(id)}
                refresh={dataRefresh}
                noResultText="Oops...It is empty here..."
                noResultText2="Add a new Service Provider to get started."
                columns={isMobile ? columns : [...columns, actionRow]}
                headerContent={
                    <CardHeader
                        title={<PageTitleWithToolTip pageTitle="Service Providers" />}
                        sx={{
                            "& .MuiCardHeader-action": { display: "flex" },
                            "& .MuiCardHeader-content": { maxWidth: "100%", textWrap: "nowrap" }
                        }}
                    />
                }
                {...(showAssign && {
                    showAddButton: true,
                    MUIButtonText: "Assign Service Provider",
                    MUIButtonOnClick: onVisibleChangeState,
                })}
            />

            <CommonModal
                header="Assign Service Provider"
                open={isVisible}
                close={() => {
                    onVisibleChangeState();
                    setLocation(null);
                    setLocationHasError(false);
                }}
            >
                <Stack
                    sx={{
                        width: "501px",
                        height: "170px",
                        gap: "25px",
                        alignItems: "center",
                        justifyContent: "center",
                        marginBottom: "20px"
                    }}
                >
                    <Select
                        required
                        variant="standard"
                        label="Service Provider"
                        value={location}
                        options={locationOptions}
                        error={locationHasError}
                        onChange={e => {
                            setLocation(e.target.value);
                            if (e.target.value !== "") {
                                setLocationHasError(false);
                            }
                        }}
                        width={70} />
                    <PrimaryButton
                        useLargeButton
                        width={70}
                        buttonText="Assign"
                        onClick={onSubmit}
                    />
                </Stack>
            </CommonModal>
        </>
    )
};

export default Providers;
